import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { AgGridCommon } from 'ag-grid-community/dist/lib/interfaces/iCommon'
import { Box, Typography } from '@mui/material'
import {
  ColDef,
  GridApi,
  GridOptions,
  IServerSideGetRowsParams,
  ISimpleFilterModel,
  SortModelItem,
} from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { APIErrorResponse } from '@archax/shared-types'
import { useSearchParams } from 'react-router-dom'
import RefreshIcon from '@mui/icons-material/Refresh'
import { downloadCSV } from '../../api/axios'
import { DEFAULT_PAGE_SIZE } from '../../constants'
import DownloadIcon from '../DownloadIcon/DownloadIcon'
import onApiError from '../../util/on-api-error'

export interface ServerSideGridProps {
  gridOptions?: Partial<GridOptions>
  queryFn: (params: any) => Promise<AxiosResponse<any, any>>
  csvExportUrlGetter?: (params: any) => string
  createCSVExport?: (params: any) => Promise<void>
  setParentGridApi?: Dispatch<SetStateAction<GridApi | null>>
  mutateFilters?: (filters: any) => any
  onGetRowsSuccess?: (data: any, params: IServerSideGetRowsParams) => void
  title?: string
  responsePath?: string
  showOverlayLoading?: boolean
  queryParamsEnabled?: boolean
  showRefreshIcon?: boolean
}

const ServerSideGrid: React.FC<ServerSideGridProps> = ({
  gridOptions,
  queryFn,
  csvExportUrlGetter,
  createCSVExport,
  setParentGridApi,
  mutateFilters,
  onGetRowsSuccess,
  title,
  responsePath,
  showOverlayLoading,
  queryParamsEnabled,
  showRefreshIcon,
}): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [preparingParams, setPreparingParams] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  const sortParam = searchParams.get('sort')
  const { onGridReady: parentOnGridReady, ...restGridOptions } = {
    ...(gridOptions || {}),
    columnDefs:
      queryParamsEnabled && !!sortParam
        ? (gridOptions?.columnDefs || []).map((it: any) => {
            const col = {
              ...it,
              sort: null,
            }
            if (sortParam && sortParam.indexOf(it.field) >= 0) {
              col.sort = sortParam.indexOf(it.field) === 0 ? 'asc' : 'desc'
            }
            return col
          })
        : gridOptions?.columnDefs,
  }

  const [gridApi, setGridApi] = useState<null | GridApi>(null)

  const buildFilters = useCallback(
    (filterModel: ISimpleFilterModel): object => {
      const filters = Object.entries(filterModel).reduce((acc, [paramName, filterParams]) => {
        const { filter: value, filterTo: valueTo, values, filterType, type, dateFrom, dateTo } = filterParams
        if (filterType === 'date') {
          if (type === 'inRange') {
            acc[`${paramName}[gte]`] = dateFrom
            acc[`${paramName}[lte]`] = dateTo
          }
          if (type === 'lessThanOrEqual') {
            acc[`${paramName}[lte]`] = dateFrom
          }
          if (type === 'greaterThanOrEqual') {
            acc[`${paramName}[gte]`] = dateFrom
          }
        } else if (filterType === 'number') {
          if (type === 'inRange') {
            acc[`${paramName}[gte]`] = value
            acc[`${paramName}[lte]`] = valueTo
          }
          if (type === 'lessThanOrEqual') {
            acc[`${paramName}[lte]`] = value
          }
          if (type === 'greaterThanOrEqual') {
            acc[`${paramName}[gte]`] = value
          }
        } else {
          acc[paramName] = value || values
        }

        return acc
      }, {} as { [key: string]: string | string[] })

      return mutateFilters ? mutateFilters(filters) : filters
    },
    [mutateFilters],
  )

  const buildSort = useCallback((sortModel: SortModelItem[]) => {
    if (!sortModel.length) {
      return null
    }
    const { colId: field, sort: direction } = sortModel[0]
    return { field, direction }
  }, [])

  const defaultColDef: ColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 90,
      filter: true,
      floatingFilter: true,
      ...gridOptions?.defaultColDef,
      resizable: true,
    }),
    [gridOptions?.defaultColDef],
  )

  const dataSource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        setIsLoading(true)
        const { startRow, filterModel, sortModel } = params.request
        let sort = buildSort(sortModel)
        const filters = buildFilters(filterModel)
        try {
          const response = await queryFn({
            offset: startRow,
            limit: DEFAULT_PAGE_SIZE,
            ...(sort && { sort }),
            ...filters,
          })
          const body = !responsePath ? response.data : response.data[responsePath]
          if (response.status === 200) {
            params.success({
              rowData: body.data,
              rowCount: body.total,
            })
            onGetRowsSuccess?.(body, params)
          } else {
            params.fail()
          }
        } catch (error) {
          onApiError(error as AxiosError<APIErrorResponse>)
        } finally {
          setIsLoading(false)
        }
      },
    }),
    [queryFn, buildSort, buildFilters, onGetRowsSuccess],
  )

  const handleCSVDownload = () => {
    const sort = buildSort(gridApi!['serverSideRowModel'].storeParams.sortModel)
    const filters = buildFilters(gridApi!.getFilterModel())
    const csvUrl = csvExportUrlGetter!({ ...(sort && { sort }), ...filters })
    return downloadCSV(csvUrl)
  }

  const handleCreateCSVExport = async () => {
    try {
      const sort = buildSort(gridApi!['serverSideRowModel'].storeParams.sortModel)
      const filters = buildFilters(gridApi!.getFilterModel())
      await createCSVExport!({ ...(sort && { sort }), ...filters })
      toast.success('Generating your CSV file. This may take a moment.')
    } catch (error) {
      toast.error('Failed to generate CSV file. An unexpected error occurred. Please contact support for assistance.')
    }
  }

  const onGridReady = useCallback(
    (params: AgGridCommon<{}, {}>) => {
      if (!gridApi) {
        setGridApi(params.api)
        setParentGridApi && setParentGridApi(params.api)
      }
      if (parentOnGridReady) {
        parentOnGridReady(params as any)
      }

      const filter = searchParams.get('filter')
      if (queryParamsEnabled && filter) {
        const filters = filter.split('|')
        const filterModel = filters.reduce((f, it) => {
          const [property, filterType, type, content] = it.split('<,>')
          let filter = undefined
          let values = undefined
          let dateFrom = undefined
          let dateTo = undefined
          if (filterType === 'text') {
            filter = content
          }
          if (filterType === 'set') {
            values = content.split('<;>')
          }
          if (filterType === 'date') {
            dateFrom = content.split('<;>')[0]
            dateTo = content.split('<;>')[1]
          }
          return {
            ...f,
            [property]: {
              filterType,
              type: type || undefined,
              filter,
              values,
              dateFrom,
              dateTo,
            },
          }
        }, {} as any)

        params.api.setFilterModel(filterModel)
        params.api.onFilterChanged()
      }
    },
    [gridApi, setParentGridApi, parentOnGridReady],
  )

  const onPageChange = (event: any) => {
    if (event.newPage) {
      searchParams.set('page', event.api?.paginationProxy.currentPage)
      setSearchParams(searchParams)
    }
  }

  const onSortChanged = (event: any) => {
    let hasSort = false
    ;(event?.columnApi?.getColumnState() || []).forEach((it: any) => {
      if (it.sort && it.colId) {
        searchParams.set('sort', `${it.sort === 'desc' ? '-' : ''}${it.colId}`)
        setSearchParams(searchParams)
        hasSort = true
      }
    })
    if (!hasSort) {
      searchParams.delete('sort')
      setSearchParams(searchParams)
    }
  }

  const onFilterChanged = (event: any) => {
    const filterModel = event.api.getFilterModel()
    const filters = [] as string[]
    Object.entries(filterModel).forEach(([k, v]) => {
      const { filterType, type, filter, values, dateFrom, dateTo } = v as any
      let content = `${k}<,>${filterType}<,>${type || ''}`
      if (values) {
        content = `${content}<,>${values.join('<;>')}`
      } else if (dateFrom || dateTo) {
        content = `${content}<,>${[dateFrom, dateTo].join('<;>')}`
      } else {
        content = `${content}<,>${filter}`
      }
      filters.push(content)
    })
    if (filters.length) {
      searchParams.set('filter', filters.join('|'))
      setSearchParams(searchParams)
    } else {
      searchParams.delete('filter')
      setSearchParams(searchParams)
    }
  }

  const defaultGridOptions: GridOptions = useMemo(
    () => ({
      rowModelType: 'serverSide',
      domLayout: 'autoHeight',
      defaultColDef,
      pagination: true,
      paginationPageSize: DEFAULT_PAGE_SIZE,
      cacheBlockSize: DEFAULT_PAGE_SIZE,
      maxBlocksInCache: 1,
      onGridReady,
      onPaginationChanged(event) {
        if (queryParamsEnabled) {
          onPageChange(event)
        }
      },
      onSortChanged(event) {
        if (queryParamsEnabled) {
          onSortChanged(event)
        }
      },
      onFilterChanged(event) {
        if (queryParamsEnabled) {
          onFilterChanged(event)
        }
      },
    }),
    [onGridReady, defaultColDef],
  )

  useEffect(() => {
    if (isLoading) {
      if (showOverlayLoading) {
        gridApi?.showLoadingOverlay()
      }
    } else {
      if (queryParamsEnabled && preparingParams) {
        if (!Number.isNaN(searchParams.get('page'))) {
          gridApi?.paginationGoToPage(Number(searchParams.get('page')))
        }
        setPreparingParams(false)
      } else {
        gridApi?.hideOverlay()
      }
    }
  }, [isLoading, preparingParams, gridApi])

  return (
    <Box width={'100%'} height={'100%'}>
      {(title || [createCSVExport, csvExportUrlGetter, showRefreshIcon].some((it) => !!it)) && (
        <Box
          display={'flex'}
          justifyContent={title ? 'space-between' : 'flex-end'}
          alignContent={'center'}
          mb={title && 4}
        >
          {title && <Typography variant="h5">{title}</Typography>}
          <RefreshIcon
            sx={{ cursor: 'pointer', m: 1, color: 'secondary.main' }}
            onClick={() => gridApi?.refreshServerSide()}
          ></RefreshIcon>
          {createCSVExport && <DownloadIcon onClick={handleCreateCSVExport}></DownloadIcon>}
          {csvExportUrlGetter && <DownloadIcon onClick={handleCSVDownload}></DownloadIcon>}
        </Box>
      )}
      <Box role="table-grid-wrapper" width={'100%'} height={'100%'} className="ag-theme-material">
        <AgGridReact
          serverSideDatasource={dataSource}
          gridOptions={{ ...defaultGridOptions, ...restGridOptions }}
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default ServerSideGrid
